import React, { useState } from 'react';
import { Edit, Trash2, Eye, FileText, Calendar, AlertCircle, CheckCircle, Clock, Search } from 'lucide-react';
import { Notice } from '../../types';
import { formatDate } from '../../utils/helpers';

interface NoticeListProps {
  notices: Notice[];
  loading?: boolean;
  onEdit: (notice: Notice) => void;
  onDelete: (notice: Notice) => void;
  onView: (notice: Notice) => void;
  onTogglePublish?: (notice: Notice) => void;
}

const NoticeList: React.FC<NoticeListProps> = ({
  notices,
  loading = false,
  onEdit,
  onDelete,
  onView,
  onTogglePublish
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 'bg-[#173322] text-[#8ff0b8]';
      case 'Medium':
        return 'bg-[#16212b] text-[#bfdbfe]';
      case 'High':
        return 'bg-[#332711] text-[#f9daa3]';
      case 'Urgent':
        return 'bg-[#341a17] text-[#fbccc8]';
      default:
        return 'bg-[#b9ad9b] text-[#4a4235]';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'Urgent':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusColor = (published: boolean) => {
    return published
      ? 'bg-[#173322] text-[#8ff0b8]'
      : 'bg-[#b9ad9b] text-[#4a4235]';
  };

  const getStatusIcon = (published: boolean) => {
    return published ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />;
  };

  // Filter notices based on search term, priority, and status
  const filteredNotices = notices.filter(notice => {
    const matchesSearch = notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notice.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = !filterPriority || notice.priority === filterPriority;
    const matchesStatus = !filterStatus ||
                         (filterStatus === 'published' && notice.published) ||
                         (filterStatus === 'draft' && !notice.published);

    return matchesSearch && matchesPriority && matchesStatus;
  });

  if (loading) {
    return (
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="flex items-center justify-center p-12">
          <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (notices.length === 0) {
    return (
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
          <p className="text-[#b9ad9b] text-lg">No notices found</p>
          <p className="text-[#8a7f6e] text-sm">Create your first notice to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
      <div className="p-6 border-b border-[#3a3122]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[#f3ede2]">Notice Board</h2>
            <p className="text-[#b9ad9b] text-sm mt-1">Manage school announcements and notices</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8a7f6e]" />
              <input
                type="text"
                placeholder="Search notices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
              />
            </div>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Priorities</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#211c15]">
            <tr>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Notice</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Priority</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Date</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Status</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Attachment</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Created</th>
              <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#3a3122]">
            {filteredNotices.map((notice) => (
              <tr key={notice.id} className="hover:bg-[#28221a]">
                <td className="py-4 px-6">
                  <div>
                    <div className="font-medium text-[#f3ede2] line-clamp-1">{notice.title}</div>
                    <div className="text-sm text-[#b9ad9b] line-clamp-2 mt-1">
                      {notice.description}
                    </div>
                    <div className="text-xs text-[#8a7f6e] mt-1">
                      ID: {notice.noticeId}
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getPriorityColor(notice.priority)}`}>
                      {getPriorityIcon(notice.priority)}
                      <span>{notice.priority}</span>
                    </span>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-[#8a7f6e]" />
                    <span className="text-[#b9ad9b]">{formatDate(notice.date)}</span>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getStatusColor(notice.published)}`}>
                      {getStatusIcon(notice.published)}
                      <span>{notice.published ? 'Published' : 'Draft'}</span>
                    </span>
                  </div>
                </td>
                <td className="py-4 px-6">
                  {notice.attachmentURL ? (
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-[#4ade80]" />
                      <span className="text-sm text-[#4ade80]">Attached</span>
                    </div>
                  ) : (
                    <span className="text-[#8a7f6e] text-sm">No attachment</span>
                  )}
                </td>
                <td className="py-4 px-6">
                  <div className="text-[#b9ad9b] text-sm">
                    <div>{formatDate(notice.createdAt)}</div>
                    <div className="text-xs text-[#8a7f6e]">by {notice.createdBy}</div>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onView(notice)}
                      className="p-2 text-[#8a7f6e] hover:text-[#60a5fa] hover:bg-[#16212b] rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(notice)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f0b64c] hover:bg-[#332711] rounded-lg transition-colors"
                      title="Edit Notice"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {onTogglePublish && (
                      <button
                        onClick={() => onTogglePublish(notice)}
                        className={`p-2 rounded-lg transition-colors ${
                          notice.published
                            ? 'text-[#8a7f6e] hover:text-[#f0b64c] hover:bg-[#332711]'
                            : 'text-[#8a7f6e] hover:text-[#4ade80] hover:bg-[#173322]'
                        }`}
                        title={notice.published ? 'Unpublish' : 'Publish'}
                      >
                        {notice.published ? <Clock className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(notice)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                      title="Delete Notice"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredNotices.length === 0 && notices.length > 0 && (
        <div className="text-center py-8">
          <FileText className="w-8 h-8 text-[#8a7f6e] mx-auto mb-2" />
          <p className="text-[#b9ad9b]">No notices match your current filters</p>
          <p className="text-[#8a7f6e] text-sm">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default NoticeList;